import NoDataFound from "components/common/NoDataFound";
import React, { useEffect, useState } from "react";
import { Container, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllWorkspaces,
  getEnableDisableSpaceStatus,
  updateEnableDisableSpaceStatus,
} from "store/actions/workspaceSlice";
import { showAlert } from "store/reducers/alertReducer";
import Spinners from "components/common/Spinners";
import Paginations from "components/common/Paginations";
import { getBranchListWithCity } from "store/actions/branchSlice";
import BranchDropdown from "pages/Dashboard/Overview/BranchDropdown";
import SpaceShimmer from "components/ShimmerEffects/SpaceShimmer";
import { InputSwitch } from "primereact/inputswitch";
import eye from "assets/images/icons/eye.svg";
import orderChange from "assets/images/icons/orderChange.svg";
import HotDeskCard from "./HotDeskCard";

const HotDesks = () => {
  const { t } = useTranslation();
  document.title = t("Hot Desks | Da Nonna");

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { search } = useLocation();
  const pageNumber = Number(new URLSearchParams(search).get("page")) || 1;

  const {
    isLoading,
    loading,
    workspaces,
    drafts,
    perPage,
    totalCount,
    totalPages,
    currentPage,
    spaceStatus,
    currentSpaceStatus,
  } = useSelector((state) => state.Workspace);

  const { branchesWithCity } = useSelector((state) => state.Branches);

  const [selectedBranches, setSelectedBranches] = useState([]);

  const matchedSpace = currentSpaceStatus?.find(
    (space) => space.name === "hotDesk"
  );
  const [displayOrder, setDisplayOrder] = useState(null);

  useEffect(() => {
    if (matchedSpace) {
      setDisplayOrder(matchedSpace.displayOrder);
    }
  }, [matchedSpace]);

  useEffect(() => {
    getAllData();

    const fetchBranchesWithCity = () => {
      dispatch(getBranchListWithCity());
    };

    fetchBranchesWithCity();
  }, []);

  const getAllData = (page) => {
    const payload = {
      spaceTypeInt: 5,
      perPage: perPage,
      pageNo: page || pageNumber,
      status: -1,
      orderBy: -1,
      keyword: "",
      dateRange: {},
      branches: selectedBranches || [],
    };
    dispatch(getAllWorkspaces(payload));
    dispatch(getEnableDisableSpaceStatus());
    navigate(`/bookings/hot-desks?page=${page || pageNumber}`);
  };

  const handleSelectionChange = (newSelected) => {
    setSelectedBranches(newSelected);
  };

  const handleBranchApply = () => {
    getAllData();
  };

  const updateSpaceStatus = (id, isHide, newOrder) => {
    const payload = {
      id,
      displayOrder: newOrder,
      isHide,
    };
    dispatch(updateEnableDisableSpaceStatus(payload))
      .then((action) => {
        const { statusCode, success, message } = action?.payload;
        if (statusCode === 200 && success) {
          dispatch(
            showAlert({ text: "Status updated Successfully!", type: "success" })
          );
        } else {
          dispatch(showAlert({ text: message, type: "error" }));
        }
      })
      .catch((err) => {});
  };

  return (
    <React.Fragment>
      <div className="page-content common_page">
        <Container fluid>
          <Row>
            <div className="g_between">
              <h1 className="main_title">{t("Hot Desks")}</h1>

              <div className="g_end gap-3">
                <BranchDropdown
                  data={branchesWithCity}
                  label={t("Branch")}
                  allLabel={t("Choose All Branches")}
                  resetLabel={t("Reset all")}
                  onSelectionChange={handleSelectionChange}
                  selectedData={selectedBranches}
                  handleApply={handleBranchApply}
                />
                <button
                  className="main_btn"
                  onClick={() => navigate("/bookings/hot-desks/add-new")}
                >
                  {t("Add New")}
                </button>
              </div>
            </div>
            <div className="common_space_app_settings row">
              <div className="col-lg-6">
                <label htmlFor="">
                  <img
                    src={eye}
                    width={15}
                    height={15}
                    className="me-2"
                    alt=""
                  />
                  {t("Show in App")}
                </label>
                <div className="checkbox_div">
                  <InputSwitch
                    checked={!matchedSpace?.isHide ?? false} // use matchedSpace
                    onChange={() => {
                      if (!matchedSpace) return;

                      // Toggle isHide and send current displayOrder
                      updateSpaceStatus(
                        matchedSpace._id,
                        !matchedSpace.isHide,
                        matchedSpace.displayOrder
                      );
                    }}
                  />
                  <h2 className="fs_16 ms-2">
                    {t(
                      "Turn on to display the “Hot Desks” page in the app. Turn off to hide it"
                    )}
                  </h2>
                </div>
              </div>

              <div className="col-lg-6">
                <label htmlFor="">
                  <img
                    src={orderChange}
                    width={15}
                    height={15}
                    className="me-2"
                    alt=""
                  />
                  {t("Display Order in App")}
                </label>
                <div className="">
                  <select
                    value={displayOrder} // use state
                    onChange={(e) => {
                      const newOrder = Number(e.target.value);
                      setDisplayOrder(newOrder);
                      if (!matchedSpace) return;

                      updateSpaceStatus(
                        matchedSpace._id,
                        matchedSpace.isHide,
                        newOrder
                      );
                    }}
                  >
                    {[1, 2, 3, 4, 5].map((num) => (
                      <option key={num} value={num}>
                        {num}
                      </option>
                    ))}
                  </select>
                  <p className="short_desc">
                    {t(
                      "Choose the position of this page in the app menu. 1 shows first, higher numbers appear later"
                    )}
                  </p>
                </div>
              </div>
            </div>
          </Row>
          <div className="common_wrapper">
            <Row>
              {loading ? (
                <SpaceShimmer col={4} />
              ) : workspaces?.length === 0 ? (
                <NoDataFound />
              ) : (
                workspaces?.map((item) => (
                  <HotDeskCard
                    key={item?.id}
                    item={item}
                    isLoading={isLoading}
                  />
                ))
              )}
            </Row>
          </div>
          <h2 className="main_title">{t("Draft")}</h2>
          <div className="common_wrapper">
            {loading ? (
              <SpaceShimmer col={4} />
            ) : drafts?.length === 0 ? (
              <NoDataFound text={t("No drafts saved yet")} />
            ) : (
              <Row>
                {drafts?.map((item) => (
                  <HotDeskCard
                    key={item?.id}
                    item={item}
                    className="draft"
                    isLoading={isLoading}
                  />
                ))}
              </Row>
            )}
          </div>
          {!loading && (
            <Paginations
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={(page) => {
                getAllData(page);
              }}
              totalCount={totalCount}
            />
          )}
        </Container>
      </div>
    </React.Fragment>
  );
};

export default HotDesks;
